import { Link, useSearchParams } from 'react-router-dom';
import { useActiveLeague } from '../hooks/useActiveLeague';
import React, { useState, useEffect, useCallback, useRef } from "react";
import InfiniteScroll from 'react-infinite-scroll-component';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';

import '../styles/transfers.css';
import '../styles/stats-table.css';

import { findObjectByKeyValue, ATTRIBUTE_MAPPING, numToPrice } from '../components/utils.js';
import {RangeSlider} from "../components/transfers/RangeSlider";
import {TeamDropdown} from "../components/common/Dropdown.jsx";
import {PlayersBlock} from "../components/transfers/PlayersBlock";
import {StatsTable} from "../components/transfers/StatsTable";
import {TransfersPageNav} from "../components/transfers/TransfersPageNav";
import {TransferFlowPopup} from "../components/transfers/Popups";
import {Popup} from "../components/common/Popup";
import {BioStatsToggle} from "../components/transfers/BioStatsToggle";
import {ZeroBudgetDropDialog, shouldOfferZeroBudgetDrop} from "../components/transfers/ZeroBudgetDropDialog";

import TransferService from '../services/transferService';
import TutorialTooltip from '../components/common/TutorialTooltip';
import { isActiveTransferBid } from '../utils/transferBidStatus.js';

// Team info will be fetched from API

function normalizeBidModalPlayer(player) {
    const currentPrice = Number.isFinite(player.currentPrice)
        ? player.currentPrice
        : player.auction?.currentPrice ?? 0;
    const auctionId = player.auction?.auctionId;
    const nextMinimumBid = Number.isFinite(player.nextMinimumBid)
        ? player.nextMinimumBid
        : undefined;

    return { ...player, currentPrice, nextMinimumBid, auctionId };
}

const TRANSFER_STATS_VIEW_ENABLED = false;

function normalizeTransferViewMode(viewMode) {
    if (TRANSFER_STATS_VIEW_ENABLED && viewMode === 'stats') {
        return 'stats';
    }

    return 'bio';
}

function TransfersPage() {
    const { activeLeague } = useActiveLeague();
    const showFields = {
        position: "Pos",
        name: "Name",
        value: "Value",
        age: "Age",
        nationality: "Nationality",
        ownership: "Team",
        height: "Height",
        weight: "Weight",
    }

    // State for metadata and pagination
    const [loadingFilters, setLoadingFilters] = useState(true);
    // Indicates that filter metadata has been fetched and initial filters/ranges are set
    const [metadataLoaded, setMetadataLoaded] = useState(false);
    
    // Filter metadata from backend
    const [positions, setPositions] = useState([{ id: 0, title: 'All' }]);
    const [nationalities, setNationalities] = useState([{ id: 0, title: 'All' }]);
    const [outfieldAttributes, setOutfieldAttributes] = useState([]);
    const [goalkeeperAttributes, setGoalkeeperAttributes] = useState([]);
    const [ageRange, setAgeRange] = useState({ min: 16, max: 40 });
    const [valueRange, setValueRange] = useState({ min: 0, max: 100000000 });
    
    const [teamInfo, setTeamInfo] = useState({
        boosted: false,
        startingBudget: 0,
        transferBudget: 0,
        remainingBudget: 0,
        squadSize: 0
    });
    
    // User bids state (moved here to avoid temporal dead zone error)
    const [userBids, setUserBids] = useState([]);

    // Dialog offering squad drops when the user has no budget left to bid
    const [dropDialogOpen, setDropDialogOpen] = useState(false);
    
    // Bio is the only active view while the Stats tab is a placeholder.
    const [viewMode, setViewMode] = useState('bio');
    
    // URL search parameters for state persistence
    const [searchParams, setSearchParams] = useSearchParams();
    const leagueIdParam = searchParams.get('leagueId');
    const derivedLeagueId = activeLeague?._id || leagueIdParam || null;
    const isQuickplayLeague = activeLeague?.gameMode === 'quickplay';

    const [teamOpen, setTeamOpen] = useState(() => {
        if (typeof window !== 'undefined') {
            if (window.matchMedia) {
                return window.matchMedia('(max-width: 960px)').matches;
            }
            return window.innerWidth <= 960;
        }
        return false;
    });
    
    // Initial filters; value cap will be set from API valueRange
    const initFilters = {
        name: null,
        position: null,
        nationality: null,
        minAge: 16,
        maxAge: 40,
        minVal: 0,
        maxVal: null, // set from API valueRange.max once metadata loads
        sortBy: 'value', // Changed from 'rating' to 'value' to match backend default
        sortOrder: 'desc',
        freeAgentsOnly: false
    }

    const [filters, setFilters] = useState(initFilters);

    // TanStack Query for players data with infinite scroll
    const queryKey = ['marketPlayers', derivedLeagueId, filters, metadataLoaded];
    const { 
        data, 
        isLoading, 
        error, 
        refetch, 
        fetchNextPage, 
        hasNextPage, 
        isFetchingNextPage 
    } = useInfiniteQuery({
        queryKey,
        queryFn: async ({ pageParam = 1 }) => {
            if (!metadataLoaded || !derivedLeagueId) {
                return { players: [], currentPage: 1, totalPages: 1 };
            }
            
            const response = await TransferService.getMarketPlayers({
                name: filters.name || undefined,
                position: filters.position || undefined,
                nationality: filters.nationality || undefined,
                minAge: filters.minAge,
                maxAge: filters.maxAge,
                minValue: filters.minVal,
                maxValue: filters.maxVal,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
                freeAgentsOnly: filters.freeAgentsOnly,
                leagueId: derivedLeagueId,
                page: pageParam,
                limit: 50
            }, {
                includeTeamNames: true
            });
            
            return response || { players: [], currentPage: 1, totalPages: 1 };
        },
        getNextPageParam: (lastPage) => {
            const currentPageNum = parseInt(lastPage.currentPage) || 1;
            const totalPagesNum = lastPage.totalPages || 1;
            return currentPageNum < totalPagesNum ? currentPageNum + 1 : undefined;
        },
        enabled: metadataLoaded && !!derivedLeagueId,
        keepPreviousData: true,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
    });

    // Extract data from query result with client-side safety net filtering
    const allPlayers = data?.pages.flatMap(page => page.players) || [];
    const players = filters.freeAgentsOnly 
        ? allPlayers.filter(player => player.isFreeAgent === true)
        : allPlayers;
    const loading = isLoading;

    // Fetch filter metadata from backend (Enhanced with error handling and validation)
    const fetchFilterMetadata = useCallback(async () => {
        if (!derivedLeagueId) {
            return;
        }

        setLoadingFilters(true);
        try {
            console.log("=== LOADING FILTER METADATA ===");
            
            const response = await TransferService.getMarketFilters({ leagueId: derivedLeagueId });
            console.log("Filter metadata response:", response);
            
            if (response && response.data) {
                const { 
                    positions: backendPositions, 
                    nationalities: backendNationalities, 
                    outfieldAttributes, 
                    goalkeeperAttributes, 
                    ageRange, 
                    valueRange 
                } = response.data;
                
                console.log("Received data:", {
                    positionsCount: backendPositions?.length || 0,
                    nationalitiesCount: backendNationalities?.length || 0,
                    ageRange,
                    valueRange
                });
                
                // Format positions for dropdown with validation
                const formattedPositions = [{ id: 0, title: 'All' }];
                if (Array.isArray(backendPositions)) {
                backendPositions.forEach((position, index) => {
                        if (position && typeof position === 'string') {
                    formattedPositions.push({ id: index + 1, title: position });
                        }
                });
                }
                console.log("Formatted positions:", formattedPositions);
                setPositions(formattedPositions);
                
                // Format nationalities for dropdown with validation
                const formattedNationalities = [{ id: 0, title: 'All' }];
                if (Array.isArray(backendNationalities)) {
                backendNationalities.forEach((nationality, index) => {
                        if (nationality && typeof nationality === 'string') {
                    formattedNationalities.push({ id: index + 1, title: nationality });
                        }
                });
                }
                console.log("Formatted nationalities:", formattedNationalities.length);
                setNationalities(formattedNationalities);
                
                // Set attribute lists
                setOutfieldAttributes(outfieldAttributes || []);
                setGoalkeeperAttributes(goalkeeperAttributes || []);
                
                // Set ranges with validation
                const validAgeRange = {
                    min: (ageRange?.min && ageRange.min > 0) ? ageRange.min : 16,
                    max: (ageRange?.max && ageRange.max > 0) ? ageRange.max : 40
                };
                
                const validValueRange = {
                    min: (valueRange?.min && valueRange.min > 0) ? valueRange.min : 0,
                    // Use API-provided max; fallback conservatively to 100m only if API missing
                    max: (valueRange?.max && valueRange.max > 0) ? valueRange.max : 100000000
                };
                
                console.log("Setting ranges:", { validAgeRange, validValueRange });
                setAgeRange(validAgeRange);
                setValueRange(validValueRange);
                
                // Update filters using API ranges. If previous values are unset/out of range, snap to API range.
                setFilters(prevFilters => {
                    const prevMinVal = typeof prevFilters.minVal === 'number' ? prevFilters.minVal : validValueRange.min;
                    const prevMaxVal = typeof prevFilters.maxVal === 'number' ? prevFilters.maxVal : validValueRange.max;

                    const newMinVal = Math.max(validValueRange.min, Math.min(prevMinVal, validValueRange.max));
                    // If previous maxVal was unset/null/less than min, use API max; else clamp
                    const newMaxValBase = (prevFilters.maxVal == null || prevMaxVal < validValueRange.min)
                        ? validValueRange.max
                        : prevMaxVal;
                    const newMaxVal = Math.max(validValueRange.min, Math.min(newMaxValBase, validValueRange.max));

                    const newMinAge = Math.max(validAgeRange.min, Math.min(prevFilters.minAge, validAgeRange.max));
                    const newMaxAge = Math.max(validAgeRange.min, Math.min(prevFilters.maxAge, validAgeRange.max));

                    return {
                        ...prevFilters,
                        minAge: newMinAge,
                        maxAge: newMaxAge,
                        minVal: newMinVal,
                        maxVal: newMaxVal
                    };
                });
                
            } else {
                console.error("Invalid response structure:", response);
                throw new Error("Invalid response structure from filter metadata endpoint");
            }
        } catch (err) {
            console.error('Error fetching filter metadata:', err);
            // Keep default values if metadata fetch fails
            // Intentionally not setting component error state to avoid undefined setter
        } finally {
            setLoadingFilters(false);
            // Signal that metadata is ready (used to trigger first players fetch)
            setMetadataLoaded(true);
        }
    }, [derivedLeagueId]);

    // Refactored to be reusable for fetching team info with improved error handling
    const fetchTeamInfo = useCallback(async () => {
        try {
            const leagueId = derivedLeagueId;
            const response = await TransferService.getTeamInfo(leagueId);
            if (response && response.data) {
                setTeamInfo(response.data);
            }
        } catch (err) {
            console.error('Error fetching team info:', err);
            console.warn('Failed to load team information. Some features may be limited.');
        }
    }, [derivedLeagueId]); // Added activeLeague dependency

    // Initialize state from URL parameters on mount
    useEffect(() => {
        const urlViewMode = normalizeTransferViewMode(searchParams.get('view'));
        const urlSortBy = searchParams.get('sortBy') || 'value';
        const urlSortOrder = searchParams.get('sortOrder') || 'desc';
        const urlPosition = searchParams.get('position');
        const urlNationality = searchParams.get('nationality');
        const urlName = searchParams.get('name');
        const urlMinAge = parseInt(searchParams.get('minAge'), 10);
        const urlMaxAge = parseInt(searchParams.get('maxAge'), 10);
        const urlMinVal = parseInt(searchParams.get('minVal'), 10);
        const urlMaxVal = parseInt(searchParams.get('maxVal'), 10);
        const urlFreeAgentsOnly = searchParams.get('freeAgentsOnly') === 'true' || searchParams.get('freeAgentsOnly') === '1';
        
        console.log('Initializing from URL:', { urlViewMode, urlSortBy, urlPosition, urlFreeAgentsOnly });
        
        setViewMode(urlViewMode);
        setFilters(prev => ({
            ...prev,
            sortBy: urlSortBy,
            sortOrder: urlSortOrder,
            position: urlPosition || null,
            nationality: urlNationality || null,
            name: urlName || null,
            freeAgentsOnly: urlFreeAgentsOnly,
            // Apply range filters only if valid numbers were provided in the URL
            minAge: !isNaN(urlMinAge) ? urlMinAge : prev.minAge,
            maxAge: !isNaN(urlMaxAge) ? urlMaxAge : prev.maxAge,
            minVal: !isNaN(urlMinVal) ? urlMinVal : prev.minVal,
            maxVal: !isNaN(urlMaxVal) ? urlMaxVal : prev.maxVal,
        }));
    }, []); // Only run once on mount

    // Fetch filter metadata when league changes
    useEffect(() => {
        if (derivedLeagueId) {
            fetchFilterMetadata();
        }
    }, [derivedLeagueId, fetchFilterMetadata]);

    // New: fetch team info once the active league is resolved or changes
    useEffect(() => {
        if (derivedLeagueId) {
            fetchTeamInfo();
        }
    }, [derivedLeagueId, fetchTeamInfo]);

    // Fetch user's existing bids
    const fetchUserBids = useCallback(async () => {
        if (!derivedLeagueId) return;
        try {
            const response = await TransferService.getBids(derivedLeagueId);
            setUserBids((response.outgoing || []).filter(isActiveTransferBid));
        } catch (error) {
            console.error('Error fetching user bids:', error);
        }
    }, [derivedLeagueId]);

    useEffect(() => {
        if (derivedLeagueId) {
            fetchUserBids();
        }
    }, [derivedLeagueId, fetchUserBids]);



    // Update URL when state changes (debounced)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const newParams = new URLSearchParams();
            newParams.set('view', viewMode);
            newParams.set('sortBy', filters.sortBy);
            newParams.set('sortOrder', filters.sortOrder);
            
            if (filters.position) newParams.set('position', filters.position);
            if (filters.nationality) newParams.set('nationality', filters.nationality);
            if (filters.name) newParams.set('name', filters.name);
            if (filters.freeAgentsOnly) newParams.set('freeAgentsOnly', 'true');

            // Always include numeric range filters to ensure they persist across tabs
            newParams.set('minAge', filters.minAge);
            newParams.set('maxAge', filters.maxAge);
            if (Number.isFinite(filters.minVal)) newParams.set('minVal', filters.minVal);
            if (Number.isFinite(filters.maxVal)) newParams.set('maxVal', filters.maxVal);
            
            setSearchParams(newParams, { replace: true });
        }, 500); // Debounce URL updates
        
        return () => clearTimeout(timeoutId);
    }, [viewMode, filters.sortBy, filters.sortOrder, filters.position, filters.nationality, filters.name, filters.freeAgentsOnly, filters.minAge, filters.maxAge, filters.minVal, filters.maxVal, setSearchParams]);

    function setName (name) {
        if (filters.name !== name) {
            setFilters(prevFilters => ({
                ...prevFilters,
                name: name
            }));
        }
    }

    function setPosition (id) {
        const positionObj = findObjectByKeyValue(positions, 'id', id);
        const position = positionObj && positionObj.title !== "All" ? positionObj.title : null;

        if (filters.position !== position) {
            setFilters(prevFilters => ({
                ...prevFilters,
                position: position,
            }));
        }
    }
    
    function setNationality (id) {
        const nationalityObj = findObjectByKeyValue(nationalities, 'id', id);
        const nationality = nationalityObj && nationalityObj.title !== "All" ? nationalityObj.title : null;

        if (filters.nationality !== nationality) {
            setFilters(prevFilters => ({
                ...prevFilters,
                nationality: nationality,
            }));
        }
    }
    
    function setSorting(sortOption) {
        const [sortBy, sortOrder] = sortOption.split('-');
        
        if (filters.sortBy !== sortBy || filters.sortOrder !== sortOrder) {
            setFilters(prevFilters => ({
                ...prevFilters,
                sortBy: sortBy,
                sortOrder: sortOrder
            }));
        }
    }

    function setMinMaxAge(min, max) {
        if (filters.minAge !== min || filters.maxAge !== max) {
            setFilters(prevFilters => ({
                ...prevFilters,
                minAge: min,
                maxAge: max,
            }));
        }
    }

    function setMinMaxValue(min, max) {
        if (filters.minVal !== min || filters.maxVal !== max) {
            setFilters(prevFilters => ({
                ...prevFilters,
                minVal: min,
                maxVal: max,
            }));
        }
    }
    const dropRef = useRef();
    const nationalityRef = useRef();
    const sortingRef = useRef();
    const rangeAgeRef = useRef();
    const rangeValRef = useRef();

    // Sync RangeSlider UI with filter values when they change (e.g. after restoring from URL)
    useEffect(() => {
        if (metadataLoaded) {
            // Age slider
            if (rangeAgeRef.current) {
                rangeAgeRef.current.setValues(filters.minAge, filters.maxAge);
            }
            // Value slider
            if (rangeValRef.current && Number.isFinite(filters.minVal) && Number.isFinite(filters.maxVal)) {
                rangeValRef.current.setValues(filters.minVal, filters.maxVal);
            }
        }
    }, [metadataLoaded, filters.minAge, filters.maxAge, filters.minVal, filters.maxVal]);

    function clearFilters() {
        const cleared = {
            name: null,
            position: null,
            nationality: null,
            freeAgentsOnly: false,
            minAge: ageRange.min,
            maxAge: ageRange.max,
            minVal: valueRange.min,
            maxVal: valueRange.max,
            sortBy: 'value',
            sortOrder: 'desc'
        }
        
        // Update filters state first
        setFilters(cleared);
        
        // Clear UI components after a small delay to ensure they don't trigger additional state changes
        setTimeout(() => {
            dropRef.current?.clear();
            nationalityRef.current?.clear();
            sortingRef.current?.clear();
            rangeAgeRef.current?.setValues(ageRange.min, ageRange.max);
            rangeValRef.current?.setValues(valueRange.min, valueRange.max);
        }, 50);
    }

    const [filtersOpen, setFiltersOpen] = useState(false);


    const [popOpen, setPopOpen] = useState(false);
    const [popEvent, setPopEvent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const bidSelectionRequestRef = useRef(0);
    const latestBidLeagueIdRef = useRef(derivedLeagueId);

    useEffect(() => {
        latestBidLeagueIdRef.current = derivedLeagueId;
        bidSelectionRequestRef.current += 1;
        setPopOpen(false);
        setSelectedPlayer(null);
        setSubmitError(null);
    }, [derivedLeagueId]);

    const closePopup = () => {
        bidSelectionRequestRef.current += 1;
        setPopOpen(false);
        setSelectedPlayer(null);
        setSubmitError(null);
    }

    const handlePopEvent = (event) => {
        console.log('=== HANDLE POP EVENT CALLED ===');
        console.log('Event:', event);
        setPopEvent(event);
    }
    const handlePopup = (event) => {
        console.log('=== HANDLE POPUP CALLED ===');
        console.log('Event:', event);
        console.log('Current popOpen state:', popOpen);
        console.log('Setting popOpen to true...');
        setPopOpen(true);
        setPopEvent(event);
        console.log('=== HANDLE POPUP COMPLETE ===');
    }

    // Fetch fresh market data for the player before opening the bid popup.
    const openBidPopupForPlayer = useCallback(async (player) => {
        if (!player) return;

        // With no budget left, bidding on a free agent cannot succeed - offer
        // the drop dialog instead so the user can free up funds first.
        if (shouldOfferZeroBudgetDrop({ player, teamInfo, isQuickplayLeague })) {
            setDropDialogOpen(true);
            return;
        }

        const requestLeagueId = derivedLeagueId;
        const requestId = bidSelectionRequestRef.current + 1;
        bidSelectionRequestRef.current = requestId;
        setSubmitError(null);
        setSelectedPlayer(null);

        let playerForPopup = player;

        if (requestLeagueId) {
            try {
                // Retrieve the latest data (includes auction info)
                const response = await TransferService.getPlayerMarketData(player.wyId, requestLeagueId);
                // MERGE the fresh market data with existing player data to ensure all fields are present
                playerForPopup = { ...player, ...(response || {}) };
            } catch (err) {
                console.error('Failed to refresh player market data:', err);
                // Fallback: use existing data so user can still proceed
                playerForPopup = player;
            }
        }

        if (requestId !== bidSelectionRequestRef.current) {
            return;
        }

        if (requestLeagueId !== latestBidLeagueIdRef.current) {
            return;
        }

        setSelectedPlayer(normalizeBidModalPlayer(playerForPopup));
        setPopEvent('bid');
        setPopOpen(true);
    }, [derivedLeagueId, isQuickplayLeague, teamInfo]);

    const handleBoost = async (option) => {
        setPopOpen(false);
        try {
            // Call API to boost transfer budget
            const leagueId = derivedLeagueId;
            const response = await TransferService.boostTransferBudget(option, leagueId);
            if (response && response.data) {
                // Update team info with new budget
                setTeamInfo(prevInfo => ({
                    ...prevInfo,
                    transferBudget: response.data.transferBudget,
                    remainingBudget: response.data.remainingBudget,
                    boosted: true
                }));
            }
        } catch (err) {
            console.error('Error boosting budget:', err);
        }
    }

    // Function to fetch more players (for infinite scroll)
    const fetchMorePlayers = useCallback(async () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Debug scroll container when players change
    useEffect(() => {
        if (players.length > 0) {
            setTimeout(() => {
                const container = document.getElementById('scrollablePlayersList');
                if (container) {
                    console.log('=== SCROLL CONTAINER DEBUG ===');
                    console.log('Container exists:', !!container);
                    console.log('Container dimensions:', {
                        scrollHeight: container.scrollHeight,
                        clientHeight: container.clientHeight,
                        offsetHeight: container.offsetHeight,
                        scrollTop: container.scrollTop
                    });
                    console.log('Container styles:', {
                        height: getComputedStyle(container).height,
                        overflow: getComputedStyle(container).overflow,
                        maxHeight: getComputedStyle(container).maxHeight
                    });
                    console.log('Can scroll:', container.scrollHeight > container.clientHeight);
                    console.log('Players count:', players.length);
                } else {
                    console.log('=== SCROLL CONTAINER NOT FOUND ===');
                }
            }, 1000); // Wait for DOM to be ready
        }
    }, [players.length]);

    const handleSubmit = async (bidData) => {
        try {
            setSubmitting(true);
            setSubmitError(null);
            
            // Submit bid via API
            const leagueId = derivedLeagueId;
            const response = await TransferService.submitBid(bidData, leagueId);
            
            // Update UI after successful bid
            if (response) {
                // Refresh team info, player list and the user's bids in parallel
                await Promise.all([fetchTeamInfo(), refetch(), fetchUserBids()]);

                // Close popup and reset state
                setPopOpen(false);
                setSelectedPlayer(null);
            }
        } catch (err) {
            setSubmitError(err.response?.data?.error || err.message || 'Failed to submit bid');
            console.error('Error submitting bid:', err);
            // Re-throw the error so that the calling code can handle it
            throw err;
        } finally {
            setSubmitting(false);
        }
    }

    // After a drop from the zero-budget dialog: refresh budget/squad, the
    // market list (the dropped player becomes a free agent) and user bids.
    const handleDroppedFromDialog = async () => {
        await Promise.all([fetchTeamInfo(), refetch(), fetchUserBids()]);
    };

    // Generate sorting options including attributes
    const generateSortingOptions = () => {
        const basicOptions = [
            { id: 0, title: 'Value (High to Low)', value: 'value-desc' },
            { id: 1, title: 'Value (Low to High)', value: 'value-asc' },
            { id: 2, title: 'Age (Youngest)', value: 'age-asc' },
            { id: 3, title: 'Age (Oldest)', value: 'age-desc' },
            { id: 4, title: 'Name (A-Z)', value: 'name-asc' },
            { id: 5, title: 'Name (Z-A)', value: 'name-desc' }
        ];
        
        let nextId = basicOptions.length;
        
        // Use hardcoded attributes from ATTRIBUTE_MAPPING instead of empty backend arrays
        Object.values(ATTRIBUTE_MAPPING).forEach(attr => {
            basicOptions.push(
                { id: nextId++, title: `${attr} (High to Low)`, value: `${attr}-desc` },
                { id: nextId++, title: `${attr} (Low to High)`, value: `${attr}-asc` }
            );
        });
        
        return basicOptions;
    };

    // Add helper functions to find dropdown IDs based on filter values
    const getSelectedPositionId = () => {
        if (!filters.position) return null;
        const positionObj = positions.find(pos => pos.title === filters.position);
        return positionObj ? positionObj.id : null;
    };

    const getSelectedNationalityId = () => {
        if (!filters.nationality) return null;
        const nationalityObj = nationalities.find(nat => nat.title === filters.nationality);
        return nationalityObj ? nationalityObj.id : null;
    };

    // Add this helper function near generateSortingOptions()
    const getCurrentSortingId = () => {
        const sortOptions = generateSortingOptions();
        const currentSortString = `${filters.sortBy}-${filters.sortOrder}`;
        const matchingOption = sortOptions.find(opt => opt.value === currentSortString);
        return matchingOption ? matchingOption.id : null;
    };

    // JSX Rendering

    const showQuickplayNextMatchBanner = Boolean(
        activeLeague?.gameMode === 'quickplay' && activeLeague?.slug
    );

        return (
            <div className="page-market">

                <div className="wrapper">
                    {showQuickplayNextMatchBanner && (
                        <section
                            className="quickplay-next-match-banner"
                            aria-label="Quick Play next steps"
                        >
                            <h2>What's next?</h2>
                            <p>
                                Create your own league, invite your friends, and battle them for the title all season.
                            </p>
                            <p className="quickplay-next-match-secondary">
                                Or jump straight into your next Quick Play match.
                            </p>
                            <div className="quickplay-next-match-actions">
                                <Link className="submit-btn" to="/play-with-friends">
                                    CREATE A LEAGUE WITH FRIENDS
                                </Link>
                                <Link
                                    className="submit-btn trans-btn white"
                                    to={`/${activeLeague.slug}/table`}
                                >
                                    PLAY YOUR NEXT MATCH
                                </Link>
                            </div>
                        </section>
                    )}

                    <h1>
                        TRANSFER MARKET
                    </h1>

                <TransfersPageNav />

                <div className="market-grid">

                    <div className="filters-col">

                        <div className={`filters ${filtersOpen ? 'active' : ''}`}>
                            <button className="show-filters"
                                    onClick={() => {setFiltersOpen(true)}}
                            >
                                SHOW FILTERS
                            </button>

                            <div className="form-block">
                                <div className="filters-container">
                                    <div className="form-group search-group">
                                        <label htmlFor="i1">SEARCH BY NAME *</label>
                                        <input id="i1" type="text"
                                               value={filters.name === null ? '' : filters.name}
                                               onChange={(event) => {setName(event.target.value)}}
                                        />
                                        <span className="ico icon-search" />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="s1">POSITION</label>
                                        {loadingFilters ? (
                                            <div className="loading-dropdown">Loading positions...</div>
                                        ) : (
                                            <TeamDropdown 
                                                ref={dropRef} 
                                                props={positions} 
                                                selectedId={getSelectedPositionId()}
                                                setId={(id) => setPosition(id)} 
                                                startVal={'POSITION'}
                                            />
                                        )}
                                    </div>
                                    
                                    <div className="form-group">
                                        <label htmlFor="s2">NATIONALITY</label>
                                        {loadingFilters ? (
                                            <div className="loading-dropdown">Loading nationalities...</div>
                                        ) : (
                                            <TeamDropdown 
                                                ref={nationalityRef} 
                                                props={nationalities} 
                                                selectedId={getSelectedNationalityId()}
                                                setId={(id) => setNationality(id)} 
                                                startVal={'NATIONALITY'}
                                            />
                                        )}
                                    </div>
                                    
                                    <div className="form-group">
                                        <label htmlFor="s3">SORT BY</label>
                                        {loadingFilters ? (
                                            <div className="loading-dropdown">Loading sort options...</div>
                                        ) : (
                                            <TeamDropdown 
                                                ref={sortingRef} 
                                                props={generateSortingOptions()} 
                                                selectedId={getCurrentSortingId()}
                                                setId={(id) => {
                                                    const sortOptions = generateSortingOptions();
                                                    const selectedOption = sortOptions.find(opt => opt.id === id);
                                                    if (selectedOption) {
                                                        setSorting(selectedOption.value);
                                                    }
                                                }} 
                                                startVal={'SORT BY'}
                                            />
                                        )}
                                    </div>

                                    <div className="range-sliders">
                                        <div className="form-group range-block">
                                            <label className="filter-title">AGE</label>
                                            {loadingFilters ? (
                                                <div className="loading-range">Loading age range...</div>
                                            ) : (
                                                <RangeSlider
                                                    ref={rangeAgeRef}
                                                    min={ageRange.min}
                                                    max={ageRange.max}
                                                    sendMinMax={setMinMaxAge}
                                                />
                                            )}
                                        </div>

                                        <div className="form-group range-block">
                                            <label className="filter-title">VALUE</label>
                                            {loadingFilters ? (
                                                <div className="loading-range">Loading value range...</div>
                                            ) : (
                                                <RangeSlider
                                                    ref={rangeValRef}
                                                    min={valueRange.min}
                                                    max={valueRange.max}
                                                    sendMinMax={setMinMaxValue}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Free Agents Filter - Toggle */}
                                    <div className="form-group free-agents-toggle">
                                        <div className="toggle-container">
                                            <label htmlFor="freeAgentsOnly" className="filter-title">FREE AGENTS ONLY</label>
                                            <label className="switch-label" aria-label="Free Agents only">
                                                <input
                                                    id="freeAgentsOnly"
                                                    type="checkbox"
                                                    checked={filters.freeAgentsOnly}
                                                    onChange={(event) => {
                                                        setFilters(prevFilters => ({
                                                            ...prevFilters,
                                                            freeAgentsOnly: event.target.checked
                                                        }));
                                                    }}
                                                />
                                                <span className="switch" aria-hidden="true" />
                                            </label>
                                        </div>
                                    </div>

                                    {/* Bio/Stats Toggle */}
                                    <div className="form-group">
                                        <BioStatsToggle 
                                            viewMode={viewMode} 
                                            onToggle={setViewMode} 
                                            />
                                    </div>
                                </div>

                                <div className="filters-bottom">
                                    <button className="hide-filters"
                                            onClick={() => {setFiltersOpen(false)}}
                                    >
                                        HIDE FILTERS
                                    </button>

                                    <div className="reset-col">
                                        <button className="reset-filters"
                                                onClick={() => {clearFilters()}}
                                        >
                                            Clear <span className="desc-text">filters</span> <span className="ico icon-s-cross" />
                                        </button>
                                    </div>
                                </div>

                            </div>

                        </div>

                    </div>

                                {/* Wheel handler to ensure scrolling works even if some global listener prevents default */}
            <div
                className="list-col"
                id="scrollablePlayersList"
            >
                        {loading ? (
                            <div className="loading-container">
                                <p>Loading players...</p>
                            </div>
                        ) : error ? (
                            <div className="error-container">
                                <p>Error loading players: {error?.message || error}</p>
                                <button 
                                    onClick={() => {
                                        setFilters(initFilters);
                                        clearFilters();
                                    }}
                                    className="reset-filters"
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : players.length === 0 ? (
                            <div className="empty-container">
                                <p>No players found matching your filters.</p>
                                <button 
                                    onClick={() => {
                                        setFilters(initFilters);
                                        clearFilters();
                                    }}
                                    className="reset-filters"
                                >
                                    Clear Filters
                                </button>
                            </div>
                        ) : (
                            <InfiniteScroll
                                dataLength={players.length}
                                next={() => {
                                    console.log('=== INFINITE SCROLL TRIGGERED ===');
                                    console.log('Current players length:', players.length);
                                    console.log('hasNextPage:', hasNextPage);
                                    console.log('isFetchingNextPage:', isFetchingNextPage);
                                    fetchMorePlayers();
                                }}
                                hasMore={hasNextPage}
                                loader={<p style={{ textAlign: 'center' }}><b>Loading...</b></p>}
                                endMessage={
                                    <p style={{ textAlign: 'center' }}>
                                    <b>Yay! You have seen it all</b>
                                    </p>
                                }
                                scrollableTarget="scrollablePlayersList"
                            >
                                {viewMode === 'bio' && (
                                <PlayersBlock 
                                    players={players} 
                                    showFields={showFields} 
                                    onBidPlayer={openBidPopupForPlayer}
                                    userBids={userBids}
                                    />
                                )}

                                {viewMode === 'stats' && (
                                    <StatsTable 
                                        players={players}
                                        onBidPlayer={openBidPopupForPlayer}
                                        onSort={setSorting}                     // Connect to backend sort
                                        currentSortBy={filters.sortBy}          // Pass current sort state
                                        currentSortType={filters.sortOrder}     // Pass sort direction
                                        showTooltips={true}                     // Enable tooltips
                                        userBids={userBids}
                                />
                                )}
                            </InfiniteScroll>
                        )}
                    </div>

                    <div className={`team-col`}>
                        <TutorialTooltip
                            id="transfers-budget-tooltip"
                            title="Transfer budget"
                            description="The transfer market is open before the beginning of the season and during mid-season. Your squad must have at least 22 players at all times.

Each team starts with the same budget. Your team’s league performance will affect your transfer budget for the next season. "
                            className="f-right"
                        />

                        <div className={`team-container ${teamOpen ? 'active' : ''}`}  >
                            <button className="show-team"
                                    onClick={() => {setTeamOpen(true)}}
                            >
                                SHOW BUDGET
                            </button>
                            <button className="hide-team"
                                    onClick={() => {setTeamOpen(false)}}
                            >
                                HIDE BUDGET
                            </button>


                            <div className="info-row">
                                <h4>My team</h4>

                                <div className="info-container">
                                    <div className="info-block">
                                        <div className="i-title boost-title">
                                            Transfer budget
                                            {/* BOOST button temporarily hidden - will be needed in future */}
                                            {/* <button
                                                type="button"
                                                className={`submit-btn trans-btn ${teamInfo.boosted ? "disabled" : ""}`}
                                                onClick={() => {
                                                    handlePopup("boost");
                                                }}
                                            >
                                                BOOST
                                            </button> */}
                                        </div>
                                        <div className="i-value">
                                            <span className="ico icon-nova" />
                                            {numToPrice(
                                                Number.isFinite(teamInfo.startingBudget) && teamInfo.startingBudget > 0
                                                    ? teamInfo.startingBudget
                                                    : teamInfo.transferBudget
                                            )}
                                        </div>
                                    </div>
                                    <div className="info-block">
                                        <div className="i-title">Remaining budget</div>
                                        <div className="i-value">
                                            <span className="ico icon-nova" />
                                            {numToPrice(teamInfo.remainingBudget)}
                                        </div>
                                        <div className="i-note">Drop or sell players to get transfer money back.</div>
                                    </div>
                                    <div className="info-block">
                                        <div className="i-title">Squad size</div>
                                        <div className="i-value">{teamInfo.squadSize} / 22</div>
                                    </div>
                                </div>

                                <div className={`tip ${teamInfo.squadSize >= 22 ? "tip-success" : "tip-alert"}`}>
                                    Minimum squad size: 22 <span className="ico icon-r-check" />
                                </div>
                            </div>

                        </div>
                    </div>

                </div>

                {(() => {
                    console.log('=== POPUP RENDER CHECK ===');
                    console.log('popOpen:', popOpen);
                    console.log('popEvent:', popEvent);
                    console.log('selectedPlayer:', selectedPlayer);
                    console.log('Should render popup:', popOpen);
                    return null;
                })()}

                {popOpen && (
                    <>
                        {console.log('=== RENDERING POPUP ===') || null}
                        <TransferFlowPopup
                            info={teamInfo}
                            popEvent={popEvent}
                            setPopEvent={handlePopEvent}
                            popOpen={popOpen}
                            closePopup={closePopup}
                            handleSubmit={handleSubmit}
                            handleBoost={handleBoost}
                            selectedPlayer={selectedPlayer}
                            submitting={submitting}
                            submitError={submitError}
                            isQuickplayLeague={isQuickplayLeague}
                        />
                    </>
                )}

                <ZeroBudgetDropDialog
                    popOpen={dropDialogOpen}
                    close={() => setDropDialogOpen(false)}
                    players={teamInfo.players || []}
                    remainingBudget={teamInfo.remainingBudget}
                    squadSize={teamInfo.squadSize}
                    teamId={teamInfo.teamId}
                    leagueId={derivedLeagueId}
                    onDropped={handleDroppedFromDialog}
                />

            </div>
        </div>
    );
}

export { TransfersPage };
